<script setup lang="ts">
  import type { QTableProps } from 'quasar'

  import { useQuery } from '@tanstack/vue-query'

  import { listPositionsPassportIdmIdmPrivateListPositionsPost } from '@/shared/api/intranet'
  import { UButton } from '@/shared/ui/components'
  import { PencilIcon, PlusIcon, SearchIcon, UserIcon } from '@/shared/ui/svg'

  interface Position {
    id: string
    name: string
    usersCount: number
  }
  definePage({
    meta: {
      layout: 'admin',
    },
  })
  const router = useRouter()

  const searchQuery = ref('')
  const handleQueryChange = (value: string | null) => {
    if (value === null) {
      searchQuery.value = ''
    } else {
      searchQuery.value = value
    }
  }

  const { data: positions, isLoading } = useQuery({
    queryKey: ['positions'],
    queryFn: () => {
      return listPositionsPassportIdmIdmPrivateListPositionsPost({
        body: { filter: {} },
        throwOnError: true,
      })
    },
    select: (response) => response.data.positions,
  })

  const filteredPositions = computed(() => {
    return positions.value?.filter((position) => position.name.toLowerCase().includes(searchQuery.value.toLowerCase()))
  })

  const tableConfig: QTableProps['columns'] = [
    {
      name: 'name',
      label: 'admin_positions_table_name',
      align: 'left',
      field: (row: Position) => {
        return row.name
      },
    },
    {
      name: 'usersCount',
      label: 'admin_positions_table_users',
      align: 'left',
      field: (row: Position) => {
        return row.usersCount
      },
    },
    {
      name: 'actions',
      label: 'admin_positions_table_actions',
      align: 'right',
      field: (row: Position) => {
        return row.id
      },
    },
  ]

  const edit = (id: string) => {
    router.push({ name: '/admin/positions/[id]/edit', params: { id } })
  }
</script>

<template>
  <div class="flex h-full w-full flex-col gap-4">
    <div class="head">
      <div class="content flex items-center justify-between">
        <h2>{{ $t('admin_positions_title') }}</h2>
        <UButton
          class="flex items-center gap-2 px-2"
          size="small"
          type="secondary"
          @click="$router.push('/admin/positions/new')"
        >
          <PlusIcon
            width="12"
            height="12"
          />
          {{ $t('admin_positions_add_position') }}
        </UButton>
      </div>
    </div>
    <div class="mb-4 flex items-center gap-4">
      <QInput
        :model-value="searchQuery"
        class="flex-1"
        :placeholder="$t('admin_positions_search_placeholder')"
        outlined
        clearable
        dense
        @update:model-value="(value) => handleQueryChange(value as string | null)"
      >
        <template #prepend>
          <SearchIcon />
        </template>
      </QInput>
    </div>
    <QTable
      :columns="tableConfig"
      :rows="filteredPositions || []"
      hide-pagination
      :rows-per-page-options="[0]"
      :no-data-label="$t('admin_positions_no_data_available')"
      :loading="isLoading"
    >
      <template #loading>
        <QInnerLoading
          showing
          color="primary"
        />
      </template>
      <template #header-cell="headCellProps">
        <QTh :props="headCellProps">
          {{ $t(headCellProps.col.label) }}
        </QTh>
      </template>
      <template #body-cell-name="cellProps">
        <QTd>
          <div class="flex cursor-pointer items-center">
            <div>{{ cellProps.value }}</div>
          </div>
        </QTd>
      </template>
      <template #body-cell-usersCount="cellProps">
        <QTd>
          <div class="flex items-center">
            <UserIcon class="mr-1" />
            {{ cellProps.value }}
          </div>
        </QTd>
      </template>
      <template #header-cell-actions="headCellProps">
        <QTh :props="headCellProps">
          <span />
        </QTh>
      </template>
      <template #body-cell-actions="cellProps">
        <QTd>
          <div class="flex items-center justify-end">
            <PencilIcon
              class="cursor-pointer"
              @click="edit(cellProps.value)"
            />
          </div>
        </QTd>
      </template>
    </QTable>
  </div>
</template>

<style lang="scss" scoped>
  .q-table {
    @include font-montserrat-400(14px, 24px, var(--dark-black-2));
    max-height: 768px;
    min-height: 150px;
    :deep(th) {
      font-weight: 600;
    }
  }
  :deep(.q-table__bottom) {
    background-color: var(--grey);
  }
  :deep(thead) {
    background-color: var(--grey);
  }
</style>
